import { useContext, useEffect, useState, useRef, useCallback } from "react";

//Components
import CheckboxWithLabel from "./CheckboxWithLabel";
import SingleDropDown from "./SingleDropDown";
import {
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@material-ui/core";
import PaginatedTableWrapper from "./PaginatedTableWrapper";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import SearchModal from "./SearchModal";
import SearchButtons from "./SearchButtons";

//Fetching
import { useLazyQuery, useMutation } from "@apollo/client";

//Contexts
import { SetupContext } from "../Contexts/SetupContext";
import { UserContext } from "../Contexts/UserContext";

//styling
import { useStyles } from "../Styles/styles";
import { withStyles } from "@material-ui/core/styles";

//utils
import {
  parseVehicleSearch,
  vehicleSearchHeaders,
} from "../utils/SearchHelpers";
import { SPECIFIC_SETUP_LISTS_QUERY } from "../Queries/SetupListsQuery";
import { isSearchBlank, localGetData, localStoreData } from "../utils/utils";
import { VEHICLE_SEARCH_QUERY } from "../Queries/VehicleSearchQuery";
import { ADD_OR_UPDATE_VEHICLE_IN_INCIDENT } from "../Mutations/IncidentMutations/UpdateVehicleInIncident";
import HelpIconButton from "./HelpIconButton";

import moment from "moment";

const initialSearchState = {
  vehicle_role_id: "",
  vehicle_attributes: {
    vehicle_make_name: "",
    vehicle_model_name: "",
    year: "",
    vehicle_plate_name: "",
    vehicle_state_id: "",
    plate_type_id: "",
    vehicle_color_id: "",
    vehicle_vin_name: "",
    description: "",
    value: "",
    owner_last: "",
    owner_first: "",
  },
  reason_seized_id: "",
  created_at_search_start_date: "",
  created_at_search_end_date: "",
  tower_id: "",
  towing_destination_id: "",
  operator_last: "",
  operator_first: "",
  position: "",
  abandoned: "0",
  impounded: "0",
  suspect_driven: "0",
  used_in_crime: "0",
  vehicle_of_interest: "0",
};
const StyledAccordionSummary = withStyles({
  root: {
    backgroundColor: props =>
      props.nightmode === "true" ? "#616161" : "#fae8ad",
    // backgroundColor: '#fae8ad',
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  content: {
    "&$expanded": {
      // margin: '12px 0',
      margin: "0",
    },
  },
  expanded: {},
})(AccordionSummary);

const VehicleSearch = ({
  handleIncidentOpening,
  handleVehicleOpening,
  handleAddVehicleMatchToIncidentState,
  showModal,
  setShowModal,
  incidentId = null,
}) => {
  //When adding the ability to add a person to an incident use the memoizedLocked
  const classes = useStyles();
  const { user, responseErrorHandler, nightMode, onlineState } =
    useContext(UserContext);
  const { setup } = useContext(SetupContext);
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [localSpecificSetup, setLocalSpecificSetup] = useState(null);
  const [resultsPerPage, setResultsPerPage] = useState(25);
  const [searchResults, setSearchResults] = useState(null);
  const [searchParameterError, setSearchParameterError] = useState(null);
  const [showSearchOptions, setShowSearchOptions] = useState(true);

  const [searchState, setSearchState] = useState(initialSearchState);
  const dashRef = useRef(null);

  const [dashSize, setDashSize] = useState({
    width: dashRef?.current?.offsetWidth,
    height: undefined,
  });

  const { states, plateTypes, vehicleColors } = setup.setupLists || {};
  const { setupLists: specificSetupLists } = localSpecificSetup || {};
  const { vehicleRoles, reasonSeizeds, towers, towingDestinations } =
    specificSetupLists || [];

  const [getSpecificSetup] = useLazyQuery(SPECIFIC_SETUP_LISTS_QUERY, {
    variables: { originatorId: user?.id },
    fetchPolicy: { requestPolicy: "no-cache" },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      responseErrorHandler(data);
      localStoreData(data, `specific-setup-${user?.homeOriId}`);
      checkForSpecificSetup(user?.homeOriId);
    },
  });

  const [
    searchVehicles,
    { loading: vehicleSearchLoading, error: vehicleSearchError },
  ] = useLazyQuery(VEHICLE_SEARCH_QUERY, {
    variables: { originatorId: user?.id },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setSearchResults(data?.searchVehicles);
        setShowSearchOptions(false);
      }
    },
  });

  const [addVehicleToIncident] = useMutation(
    ADD_OR_UPDATE_VEHICLE_IN_INCIDENT,
    {
      onCompleted: data => {
        if (data) {
          !!handleAddVehicleMatchToIncidentState &&
            handleAddVehicleMatchToIncidentState(
              data.addOrUpdateVehicleInIncident.incidentUuid,
              data.addOrUpdateVehicleInIncident.vehicleIncidentSpecific
            );
          setShowModal(false);
        }
      },
    }
  );

  const handleAddVehicleToIncident = (vehicle, incidentId) => {
    addVehicleToIncident({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId,
          vehicleId: vehicle.id,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const handleChange = (value, name) => {
    setSearchState(prevState => {
      return { ...prevState, [name]: value };
    });
  };

  const handleAttributesChange = (value, name) => {
    setSearchState(prevState => {
      return {
        ...prevState,
        vehicle_attributes: {
          ...prevState.vehicle_attributes,
          [name]: value,
        },
      };
    });
  };

  const checkForSpecificSetup = useCallback(
    oriId => {
      if (!oriId) {
        return;
      } else {
        if (localGetData(`specific-setup-${oriId}`)) {
          const setup = localGetData(`specific-setup-${oriId}`);
          const now = moment();
          const age = now.diff(setup.timestamp, "hours");
          if (age > 8 && onlineState) {
            localStorage.removeItem(`specific-setup-${oriId}`);
            getSpecificSetup({ variables: { oriId: oriId } });
          } else {
            setLocalSpecificSetup(localGetData(`specific-setup-${oriId}`));
          }
        } else {
          getSpecificSetup({ variables: { oriId: oriId } });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onlineState]
  );

  const submitSearch = (_perPage = resultsPerPage, page = 1) => {
    const isBlank = isSearchBlank(searchState, initialSearchState);

    if (isBlank) {
      setSearchParameterError("Please enter one or more search values");
    } else {
      const searchStr = JSON.stringify(searchState);
      if (searchParameterError) setSearchParameterError(null);
      searchVehicles({
        variables: { perPage: resultsPerPage, page: page, json: searchStr },
      });
    }
    // setSearchResults(MOCK_VEHICLE_RESULTS)
  };

  useEffect(() => {
    function handleResize() {
      dashRef.current &&
        setDashSize({
          width: dashRef.current.offsetWidth,
          height: dashRef.current.offsetHeight,
        });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setDashSize({
      width: dashRef?.current?.offsetWidth,
      height: dashRef?.current?.offsetHeight,
    });
  }, [dashRef, showModal]);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  useEffect(() => {
    if (searchResults?.length < 0) {
      setSearchResults([]);
    }
  }, [searchState, resultsPerPage, searchResults?.length]);

  useEffect(() => {
    showModal && checkForSpecificSetup(user?.homeOriId);
  }, [checkForSpecificSetup, showModal, user?.homeOriId]);

  return showModal ? (
    <SearchModal
      keepMounted={true}
      showModal={showModal}
      setShowModal={setShowModal}
    >
      <form
        className={classes.paper}
        ref={dashRef}
        style={{ display: "flex", flexDirection: "column", padding: 0 }}
      >
        <Accordion
          nightmode={nightMode}
          square
          expanded={showSearchOptions}
          type="button"
          onChange={() => setShowSearchOptions(!showSearchOptions)}
          TransitionProps={{ unmountOnExit: false }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            style={{ margin: 0 }}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: nightMode ? "white" : "#303e4f",
                }}
                className={classes.expansionPanelTypography}
                data-cy="vehicle-search-header"
              >
                Vehicle Search{" "}
                {showSearchOptions ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </Typography>
              <HelpIconButton objectname="mobile_vehicle_search" />
            </Box>
          </StyledAccordionSummary>
          <AccordionDetails onClick={e => e.stopPropagation()}>
            <div
              style={{ display: "flex", flexDirection: "column", padding: 0 }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  height: "auto",
                  justifyContent: "flex-start",
                }}
              >
                <SingleDropDown
                  handleChange={e =>
                    handleChange(
                      e.target.value?.id?.toString(),
                      "vehicle_role_id"
                    )
                  }
                  stateName={"vehicle_role_id"}
                  value={vehicleRoles?.find(
                    state => state.id === searchState?.vehicle_role_id
                  )}
                  inputProps={{ style: { borderRadius: "5px" } }}
                  options={vehicleRoles}
                  title={"Role"}
                  bRadius={"5px"}
                  classStyle={classes.matchMargin}
                  inputLabel={inputLabel}
                  labelWidth={labelWidth}
                  disabled={false}
                  clearable
                />
                <TextField
                  data-cy="vehicle-make"
                  name={"vehicle_make_name"}
                  inputProps={{ "data-cy": "vehicle-make-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-make-id"
                  label="Vehicle Make"
                  value={searchState?.vehicle_attributes?.vehicle_make_name}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAttributesChange(e.target.value, "vehicle_make_name")
                  }
                />
                <TextField
                  data-cy="vehicle-model"
                  name={"vehicle_model_name"}
                  inputProps={{ "data-cy": "vehicle-model-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-model-id"
                  label="Vehicle Model"
                  value={searchState?.vehicle_attributes?.vehicle_model_name}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAttributesChange(e.target.value, "vehicle_model_name")
                  }
                />
                <TextField
                  data-cy="vehicle-year"
                  name={"year"}
                  inputProps={{ "data-cy": "vehicle-year-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-year-id"
                  label="Vehicle Year"
                  value={searchState?.vehicle_attributes?.year}
                  className={classes.matchMargin}
                  onChange={e => handleAttributesChange(e.target.value, "year")}
                />
                <TextField
                  data-cy="vehicle-plate-name"
                  name={"vehicle_plate_name"}
                  inputProps={{ "data-cy": "vehicle-make-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-make-id"
                  label="Vehicle Plate"
                  value={searchState?.vehicle_attributes?.vehicle_plate_name}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAttributesChange(e.target.value, "vehicle_plate_name")
                  }
                />
                <SingleDropDown
                  handleChange={e =>
                    handleAttributesChange(
                      e.target.value?.id?.toString(),
                      "vehicle_state_id"
                    )
                  }
                  stateName={"vehicle_state_id"}
                  value={states?.find(
                    state =>
                      state.id ===
                      searchState?.vehicle_attributes?.vehicle_state_id
                  )}
                  inputProps={{ style: { borderRadius: "5px" } }}
                  options={states}
                  title={"State"}
                  clearable
                  bRadius={"5px"}
                  classStyle={classes.matchMargin}
                  inputLabel={inputLabel}
                  labelWidth={labelWidth}
                  disabled={false}
                />
                <SingleDropDown
                  handleChange={e =>
                    handleAttributesChange(
                      e.target.value?.id?.toString(),
                      "plate_type_id"
                    )
                  }
                  stateName={"plate_type_id"}
                  value={plateTypes?.find(
                    state =>
                      state.id ===
                      searchState?.vehicle_attributes?.plate_type_id
                  )}
                  inputProps={{ style: { borderRadius: "5px" } }}
                  options={plateTypes}
                  title={"Plate Type"}
                  clearable
                  bRadius={"5px"}
                  classStyle={classes.matchMargin}
                  inputLabel={inputLabel}
                  labelWidth={labelWidth}
                  disabled={false}
                />
                <SingleDropDown
                  handleChange={e =>
                    handleAttributesChange(
                      e.target.value?.id?.toString(),
                      "vehicle_color_id"
                    )
                  }
                  stateName={"vehicle_color_id"}
                  value={vehicleColors?.find(
                    state =>
                      state.id ===
                      searchState?.vehicle_attributes?.vehicle_color_id
                  )}
                  inputProps={{ style: { borderRadius: "5px" } }}
                  options={vehicleColors}
                  title={"Color"}
                  clearable
                  bRadius={"5px"}
                  classStyle={classes.matchMargin}
                  inputLabel={inputLabel}
                  labelWidth={labelWidth}
                  disabled={false}
                />
                <TextField
                  data-cy="vehicle-vin"
                  name={"vehicle_vin_name"}
                  inputProps={{ "data-cy": "vehicle-vin-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-vin-id"
                  label="Vehicle VIN"
                  value={searchState?.vehicle_attributes?.vehicle_vin_name}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAttributesChange(e.target.value, "vehicle_vin_name")
                  }
                />
                <TextField
                  data-cy="vehicle-description"
                  name={"description"}
                  inputProps={{ "data-cy": "vehicle-description-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-description-id"
                  label="Other Description"
                  value={searchState?.vehicle_attributes?.description}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAttributesChange(e.target.value, "description")
                  }
                />
                <TextField
                  data-cy="vehicle-value"
                  name={"value"}
                  inputProps={{ "data-cy": "vehicle-value-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-value-id"
                  label=" Value"
                  value={searchState?.vehicle_attributes?.value}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAttributesChange(e.target.value, "value")
                  }
                />
                <SingleDropDown
                  handleChange={e =>
                    handleChange(
                      e.target.value?.id?.toString(),
                      "reason_seized_id"
                    )
                  }
                  stateName={"reason_seized_id"}
                  value={reasonSeizeds?.find(
                    state => state.id === searchState?.reason_seized_id
                  )}
                  inputProps={{ style: { borderRadius: "5px" } }}
                  options={reasonSeizeds}
                  title={"Reason Seized/Towed"}
                  clearable
                  bRadius={"5px"}
                  classStyle={classes.matchMargin}
                  inputLabel={inputLabel}
                  labelWidth={labelWidth}
                  disabled={false}
                />
                <SingleDropDown
                  handleChange={e =>
                    handleChange(e.target.value?.id?.toString(), "tower_id")
                  }
                  stateName={"tower_id"}
                  value={towers?.find(
                    state => state.id === searchState?.tower_id
                  )}
                  inputProps={{ style: { borderRadius: "5px" } }}
                  options={towers}
                  title={"Towed By"}
                  clearable
                  bRadius={"5px"}
                  classStyle={classes.matchMargin}
                  inputLabel={inputLabel}
                  labelWidth={labelWidth}
                  disabled={false}
                />
                <SingleDropDown
                  handleChange={e =>
                    handleChange(
                      e.target.value?.id?.toString(),
                      "towing_destination_id"
                    )
                  }
                  stateName={"towing_destination_id"}
                  value={towingDestinations?.find(
                    state => state.id === searchState?.towing_destination_id
                  )}
                  inputProps={{ style: { borderRadius: "5px" } }}
                  options={towingDestinations}
                  title={"Towed To"}
                  clearable
                  bRadius={"5px"}
                  classStyle={classes.matchMargin}
                  inputLabel={inputLabel}
                  labelWidth={labelWidth}
                  disabled={false}
                />
                <TextField
                  data-cy="vehicle-owner-last"
                  name={"owner_last"}
                  inputProps={{ "data-cy": "vehicle-owner_last-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-owner_last-id"
                  label="Owner Last"
                  value={searchState?.vehicle_attributes?.owner_last}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAttributesChange(e.target.value, "owner_last")
                  }
                />
                <TextField
                  data-cy="vehicle-owner-first"
                  name={"owner_first"}
                  inputProps={{ "data-cy": "vehicle-owner_first-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-owner_first-id"
                  label="Owner First"
                  value={searchState?.vehicle_attributes?.owner_first}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleAttributesChange(e.target.value, "owner_first")
                  }
                />
                <TextField
                  data-cy="vehicle-operator-last"
                  name={"operator_last"}
                  inputProps={{ "data-cy": "vehicle-operator_last-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-operator_last-id"
                  label="Operator Last"
                  value={searchState?.operator_last}
                  className={classes.matchMargin}
                  onChange={e => handleChange(e.target.value, "operator_last")}
                />
                <TextField
                  data-cy="vehicle-operator-first"
                  name={"operator_first"}
                  inputProps={{ "data-cy": "vehicle-operator_first-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-operator_first-id"
                  label="Operator First"
                  value={searchState?.operator_first}
                  className={classes.matchMargin}
                  onChange={e => handleChange(e.target.value, "operator_first")}
                />
                <TextField
                  data-cy="vehicle-position"
                  name={"position"}
                  inputProps={{ "data-cy": "vehicle-position-input" }}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="vehicle-position-id"
                  label="Position"
                  value={searchState?.position}
                  className={classes.matchMargin}
                  onChange={e => handleChange(e.target.value, "position")}
                />
                <CheckboxWithLabel
                  checked={searchState?.abandoned === "1" ? true : false}
                  stateName={"abandoned"}
                  onChange={e =>
                    handleChange(
                      e.target.checked === true ? "1" : "0",
                      "abandoned"
                    )
                  }
                  title={"Abandoned"}
                  formStyle={classes.formControl}
                  disabled={false}
                />
                <CheckboxWithLabel
                  checked={searchState?.impounded === "1" ? true : false}
                  stateName={"impounded"}
                  onChange={e =>
                    handleChange(
                      e.target.checked === true ? "1" : "0",
                      "impounded"
                    )
                  }
                  title={"Impounded"}
                  formStyle={classes.formControl}
                  disabled={false}
                />
                <CheckboxWithLabel
                  checked={searchState?.suspect_driven === "1" ? true : false}
                  stateName={"suspect_driven"}
                  onChange={e =>
                    handleChange(
                      e.target.checked === true ? "1" : "0",
                      "suspect_driven"
                    )
                  }
                  title={"Driven by Suspect"}
                  formStyle={classes.formControl}
                  disabled={false}
                />
                <CheckboxWithLabel
                  checked={searchState?.used_in_crime === "1" ? true : false}
                  stateName={"used_in_crime"}
                  onChange={e =>
                    handleChange(
                      e.target.checked === true ? "1" : "0",
                      "used_in_crime"
                    )
                  }
                  title={"Used in Crime"}
                  formStyle={classes.formControl}
                  disabled={false}
                />
                <CheckboxWithLabel
                  checked={
                    searchState?.vehicle_of_interest === "1" ? true : false
                  }
                  stateName={"vehicle_of_interest"}
                  onChange={e =>
                    handleChange(
                      e.target.checked === true ? "1" : "0",
                      "vehicle_of_interest"
                    )
                  }
                  title={"Vehicle of Interest"}
                  formStyle={classes.formControl}
                  disabled={false}
                />
                <div className={classes.matchMargin}>
                  <InputLabel
                    shrink={true}
                    className={classes.label}
                    data-cy={"results-per-page-label"}
                    ref={inputLabel}
                    id={"results-per-page-label"}
                  >
                    Results Per Page
                  </InputLabel>
                  <Select
                    data-cy={"results-per-page-select"}
                    id={"results-per-page-select"}
                    name={"resultPerPage"}
                    value={resultsPerPage}
                    native={false}
                    // renderValue={() => !nameFormatter ? (value?.[name] ? value[name] : " ") : nameFormatter(value)}
                    onChange={e => setResultsPerPage(e.target.value)}
                    style={{ marginTop: "0px", borderRadius: "5px" }}
                  >
                    <MenuItem
                      data-cy={"10-results-per-page"}
                      key={10}
                      value={10}
                    >
                      10
                    </MenuItem>
                    <MenuItem
                      data-cy={"25-results-per-page"}
                      key={25}
                      value={25}
                    >
                      25
                    </MenuItem>
                    <MenuItem
                      data-cy={"50-results-per-page"}
                      key={50}
                      value={50}
                    >
                      50
                    </MenuItem>
                  </Select>
                </div>
              </div>
              <SearchButtons
                loading={vehicleSearchLoading}
                submitSearch={submitSearch}
                setSearchResults={setSearchResults}
                setShowModal={setShowModal}
                resetSearchState={() => setSearchState(initialSearchState)}
                clearSearchResults={() => {
                  setSearchResults(null);
                  setSearchParameterError(null);
                }}
              />
              {searchParameterError && <p>{searchParameterError}</p>}
            </div>
          </AccordionDetails>
        </Accordion>
        {vehicleSearchError?.graphql?.errors?.map((error, index) => (
          <div key={index}>Error</div>
        ))}
        {!!searchResults && (
          <PaginatedTableWrapper
            dashSize={dashSize}
            tableHeight={dashRef?.current?.offsetHeight * 0.95}
            tableName={"Vehicle Search Results"}
            headers={vehicleSearchHeaders(incidentId)}
            userId={user?.id}
            paginatedQueryHandler={submitSearch}
            rows={
              searchResults
                ? parseVehicleSearch(
                    searchResults,
                    incidentId,
                    handleIncidentOpening,
                    handleVehicleOpening,
                    handleAddVehicleToIncident,
                    setShowModal
                  )
                : []
            }
            loading={vehicleSearchLoading}
            error={vehicleSearchError}
            paginated={true}
            rowsPerPage={resultsPerPage}
          />
        )}
      </form>
    </SearchModal>
  ) : null;
};

export default VehicleSearch;
